import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { IoCloseOutline, IoPencilOutline, IoSparklesOutline, IoTrashOutline, IoArchiveOutline, IoLinkOutline, IoChevronUpOutline, IoChevronDownOutline, IoRefreshOutline } from 'react-icons/io5';
import { useDarkMode } from '../context/DarkModeContext';
import { useToast } from '../context/ToastContext';
import { supabase } from '../utils/supabase';
import { getDateTags } from '../utils/dateTags';
import { formatDate, formatDateOnly } from '../utils/dateUtils';
import { VoiceRecordingControl } from './VoiceRecordingControl';
import { Item, ItemUpdate, Note, Review } from '../types/items';
import { LinkedContentPreview, LinkedContent } from './LinkedContentPreview';
import { useCachedData } from '../hooks/useCachedData';
import { CACHE_KEYS, CACHE_DURATIONS } from '../constants/cacheKeys';
import { ImpactMetricsSection } from './ImpactMetrics/ImpactMetricsSection';
import { ImpactHistoryEntry } from '../types/careerCard';
import { EditState } from '../types/editState';
import { hasContentChanged } from '../utils/changeDetection';
import { createLogger } from '../utils/logger';
import { SaveButton } from './SaveButton';
import { useAutoSave } from '../hooks/useAutoSave';
import { AutoSaveStatus } from './AutoSaveStatus';
import { UnsavedChangesDot } from './UnsavedChangesDot';
import { DraftRecoveryDialog } from './DraftRecoveryDialog';
import { useDraftRecovery } from '../hooks/useDraftRecovery';
import MarkdownEditor from './RetroCreator/ReflectionEditor/MarkdownEditorWrapper';
import { useAuthState } from '../hooks/useAuthState';
import { networkUtils } from '../utils/networkUtils';
import { useUserTimezone } from '../hooks/useUserTimezone';
import { useActiveCareerFocus } from '../hooks/useCareerFocus';
import { useGoals } from '../hooks/useGoals';
import { useRubric } from '../hooks/useRubric';
import { useContentLinks } from '../hooks/useContentLinks';
import GoalSelector from './CareerFocus/GoalSelector';
import RubricCriteriaSelector from './CareerFocus/RubricCriteriaSelector';
import { CheckIcon } from '@heroicons/react/24/outline';

const logger = createLogger({ module: 'UnifiedSidePanel', level: 'warn' });

const ACTIVE_TAG = 'Active';
const ARCHIVE_TAG = 'Archived';

// Content type enum values
const CONTENT_TYPE = {
  REVIEW: 'review',
  NOTE: 'note',
  WORK_EXPERIENCE: 'work_experience',
} as const;

interface UnifiedSidePanelProps {
  item: Item;
  onClose: () => void;
  onSave: (item: ItemUpdate) => Promise<Item>;
  onDelete: (id: string) => Promise<void>;
  startInEditMode?: boolean;
  skipAnimation?: boolean;
  editState: EditState<Item> | null;
  setEditState: (state: EditState<Item> | null) => void;
}

// Add helper function to format output style
const formatOutputStyle = (style: string): string => {
  return style.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

// Add type guards back
const isNote = (item: Item): item is Note => item.type === 'note';
const isReview = (item: Item): item is Review => item.type === 'review';

interface SuggestedTopics {
  prompt: string;
  suggestions: string[];
}

/**
 * UnifiedSidePanel handles the display and editing of notes and reviews.
 * 
 * Animation Behavior:
 * - When opening from empty state (no panel open): Slides in from right
 * - When switching between panels: No slide animation
 * - When closing: Always slides out to right
 * This behavior is controlled by the skipAnimation prop from the parent
 */
export const UnifiedSidePanel: React.FC<UnifiedSidePanelProps> = ({
  item,
  onClose,
  onSave,
  onDelete,
  startInEditMode = false,
  skipAnimation = false,
  editState,
  setEditState,
}) => {
  const { darkMode } = useDarkMode();
  const { showToast } = useToast();
  const [editMode, setEditMode] = useState(startInEditMode);
  const [isSuggestedTopicsExpanded, setIsSuggestedTopicsExpanded] = useState(true);
  const editorRef = useRef<{ textarea: HTMLTextAreaElement } | null>(null);
  const [editedTitle, setEditedTitle] = useState(item.title);
  const [editedContent, setEditedContent] = useState(item.content);
  const [editedCustomTags, setEditedCustomTags] = useState<string[]>(item.custom_tags || []);
  const [editedDate, setEditedDate] = useState(
    isNote(item) ? item.note_date : isReview(item) ? item.review_date : ''
  );
  const [isGeneratingTitle, setIsGeneratingTitle] = useState(false);
  const [isGeneratingTags, setIsGeneratingTags] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [suggestedTags, setSuggestedTags] = useState<string[]>([]);

  // Add state for linked content
  const [linkedWorkExperience, setLinkedWorkExperience] = useState<LinkedContent | null>(null);
  const [isLoadingLinkedContent] = useState(false);
  const [pendingWorkExperienceId, setPendingWorkExperienceId] = useState<string | null>(null);

  // Add new state for career impact and engagement level
  const [editedCareerImpact, setEditedCareerImpact] = useState(
    isNote(item) ? item.career_impact || 3 : 3
  );
  const [editedEngagementLevel, setEditedEngagementLevel] = useState(
    isNote(item) ? item.engagement_level || 3 : 3
  );

  // Add suggested topics state
  const [suggestedTopics, setSuggestedTopics] = useState<SuggestedTopics | null>(null);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  // Add state for used suggestions
  const [usedSuggestions, setUsedSuggestions] = useState<Set<string>>(new Set());

  // Add useAuthState hook
  const { user, isAuthenticated } = useAuthState();

  // Add the useUserTimezone hook
  const { formatWithTimezone, formatDateOnlyWithTimezone } = useUserTimezone();
  
  // Add career focus and rubric hooks
  const { focus } = useActiveCareerFocus();
  const { goals } = useGoals(focus?.id || null);
  const { activeRubric } = useRubric(focus?.id);
  const { getFilteredLinks } = useContentLinks();
  
  // State for linked goals and criteria
  const [linkedGoalIds, setLinkedGoalIds] = useState<string[]>([]);
  const [linkedRubricCriteria, setLinkedRubricCriteria] = useState<string[]>([]);
  const [isFocusLinkageExpanded, setIsFocusLinkageExpanded] = useState(false);
  
  // Load linked goals and rubric criteria
  useEffect(() => {
    const loadLinks = async () => {
      if (!item.id || !focus) return;

      try {
        // Load goal links
        const goalLinks = await getFilteredLinks({
          sourceType: item.type,
          sourceId: item.id,
          targetType: 'goal'
        });
        
        const goalIds = goalLinks.map(link => link.target_id);
        setLinkedGoalIds(goalIds);

        // Load rubric criterion links
        if (activeRubric) {
          const criterionLinks = await getFilteredLinks({
            sourceType: item.type,
            sourceId: item.id,
            targetType: 'rubric_criterion'
          });
          
          const criteriaIds = criterionLinks.map(link => link.target_id);
          setLinkedRubricCriteria(criteriaIds);
        }
      } catch (error) {
        console.error('Failed to load links:', error);
        setLinkedRubricCriteria([]);
      }
    };

    loadLinks();
  }, [item.id, item.type, focus, activeRubric, getFilteredLinks]);

  // Fetch work experiences using the cache system
  const { 
    data: workExperiences = [], 
    loading: workExperiencesLoading
  } = useCachedData<LinkedContent[]>(
    async () => {
      const { data, error } = await supabase
        .from('work_experience')
        .select('*')
        .order('start_date', { ascending: false });

      if (error) throw error;

      return data.map(exp => ({
        id: exp.id,
        type: 'work_experience' as const,
        preview: {
          company: exp.company,
          position: exp.position,
          date_range: {
            start: exp.start_date,
            end: exp.end_date || undefined,
          },
        },
      }));
    },
    {
      cacheKey: CACHE_KEYS.WORK_EXPERIENCES,
      maxAge: CACHE_DURATIONS.SHORT,
      initialData: [],
    }
  );

  // Fetch content links using the cache system
  const { 
    data: contentLinks = [], 
    loading: contentLinksLoading,
    refresh: refreshContentLinks
  } = useCachedData<Array<{source_id: string; target_id: string; target_type: string}>>(
    async () => {
      const { data, error } = await supabase
        .from('content_links')
        .select('*')
        .eq('source_type', item.type);

      if (error) throw error;
      return data || [];
    },
    {
      cacheKey: CACHE_KEYS.CONTENT_LINKS,
      maxAge: CACHE_DURATIONS.SHORT,
      initialData: [],
    }
  );

  // Use cached content links to find linked work experience
  useEffect(() => {
    if (!item.id || contentLinksLoading || workExperiencesLoading) {
      setLinkedWorkExperience(null);
      return;
    }

    const link = contentLinks?.find(
      link => link.source_id === item.id && link.target_type === CONTENT_TYPE.WORK_EXPERIENCE
    );

    if (!link) {
      setLinkedWorkExperience(null);
      return;
    }

    const linkedExp = workExperiences?.find(exp => exp.id === link.target_id);
    if (linkedExp) {
      setLinkedWorkExperience(linkedExp);
    }
  }, [item.id, contentLinks, workExperiences, contentLinksLoading, workExperiencesLoading]);

  const handleWorkExperienceSelect = async (workExperienceId: string) => {
    try {
      // Use user from useAuthState instead of direct auth call
      if (!isAuthenticated || !user) {
        throw new Error('No active session');
      }

      // If we don't have an item ID yet, store the work experience ID to be linked after save
      if (!item.id) {
        setPendingWorkExperienceId(workExperienceId);
        const newLinkedExp = workExperiences?.find(exp => exp.id === workExperienceId);
        if (newLinkedExp) {
          setLinkedWorkExperience(newLinkedExp);
        }
        return;
      }

      // Create new link
      const { error } = await supabase
        .from('content_links')
        .insert({
          source_id: item.id,
          source_type: item.type,
          target_id: workExperienceId,
          target_type: CONTENT_TYPE.WORK_EXPERIENCE,
          user_id: user.id,
        });

      if (error) throw error;

      // Refresh content links cache
      await refreshContentLinks();

      showToast('Successfully linked work experience', 'success');
    } catch (error) {
      console.error('Error creating work experience link:', error);
      showToast('Failed to link work experience', 'error');
    }
  };

  const handleUnlinkWorkExperience = async () => {
    if (!item.id) {
      setPendingWorkExperienceId(null);
      setLinkedWorkExperience(null);
      return;
    }

    if (!linkedWorkExperience) return;

    try {
      const { error } = await supabase
        .from('content_links')
        .delete()
        .filter('source_id', 'eq', item.id)
        .filter('source_type', 'eq', `${item.type}`)
        .filter('target_type', 'eq', `${CONTENT_TYPE.WORK_EXPERIENCE}`);

      if (error) throw error;

      // Refresh content links cache
      await refreshContentLinks();

      setLinkedWorkExperience(null);
      showToast('Successfully unlinked work experience', 'success');
    } catch (error) {
      console.error('Error unlinking work experience:', error);
      showToast('Failed to unlink work experience', 'error');
    }
  };

  useEffect(() => {
    // If we have unsaved changes and this is a different item, we should show the dialog
    if (editState?.hasUnsavedChanges && item.id !== editState.originalContent.id) {
      // Let parent handle the item switch
      return;
    }

    // Only reset states when switching items or initializing
    if (!editState || item.id !== editState.originalContent.id) {
      setEditedTitle(item.title);
      setEditedContent(item.content);
      setEditedDate(isNote(item) ? item.note_date : isReview(item) ? item.review_date : '');
      if (isNote(item)) {
        setEditedCareerImpact(item.career_impact || 3);
        setEditedEngagementLevel(item.engagement_level || 3);
      }
      let tags = item.custom_tags || [];
      
      // Remove both system tags first
      tags = tags.filter(tag => tag !== ACTIVE_TAG && tag !== ARCHIVE_TAG);
      
      // Add appropriate system tag
      const isArchived = item.custom_tags?.includes(ARCHIVE_TAG);
      if (isArchived) {
        tags = [ARCHIVE_TAG, ...tags];
      } else {
        tags = [ACTIVE_TAG, ...tags];
      }
      
      setEditedCustomTags(tags);
    }
    
    // Only reset edit mode when switching items, not when initializing edit state
    if (item.id !== editState?.originalContent.id) {
      setEditMode(startInEditMode || !item.id);
      setEditState(null);
    }
  }, [item, editState, startInEditMode]);

  useEffect(() => {
    if (editMode && !editState) {
      // Initialize edit state when entering edit mode
      const reviewFields = isReview(item) ? {
        review_text: item.content,
        review_name: item.title,
        review_date: item.review_date,
        review_goal: item.review_goal,
        output_style: item.output_style,
        word_count_target: item.word_count_target,
        context_counts: item.context_counts,
        career_card_ids: item.career_card_ids,
        note_ids: item.note_ids
      } : {};
      
      logger.debug('Initializing edit state', {
        editMode,
        hasExistingEditState: !!editState,
        itemType: item.type,
        reviewFields
      });

      // Get the current tags, ensuring Active tag is present in both original and current content
      let tags = item.custom_tags || [];
      tags = tags.filter(tag => tag !== ACTIVE_TAG && tag !== ARCHIVE_TAG);
      tags = [ACTIVE_TAG, ...tags];
      
      // Initialize edit state with the same tags for both original and current content
      const initialContent = {
        ...item,
        ...reviewFields,
        custom_tags: tags
      };
      
      // For new notes, set hasUnsavedChanges to true to enable the save button
      setEditState({
        hasUnsavedChanges: !item.id,
        originalContent: initialContent,
        currentContent: initialContent,
      });
    }
  }, [editMode, editState, item, setEditState]);

  useEffect(() => {
    if (!editState) return;

    // Only update current content if we're in edit mode
    if (editMode) {
      const currentContent = {
        ...editState.originalContent,
        title: editedTitle,
        content: editedContent,
        custom_tags: editedCustomTags,
        ...(isNote(item) ? { 
          note_date: editedDate,
          career_impact: editedCareerImpact,
          engagement_level: editedEngagementLevel,
        } : {}),
        ...(isReview(item) ? { 
          review_date: editedDate,
          review_text: editedContent,
          review_name: editedTitle
        } : {}),
      };

      const hasChanges = hasContentChanged(editState.originalContent, currentContent);
      
      if (hasChanges !== editState.hasUnsavedChanges || JSON.stringify(currentContent) !== JSON.stringify(editState.currentContent)) {
        
        setEditState({
          ...editState,
          hasUnsavedChanges: hasChanges,
          currentContent,
        });
      }
    }
  }, [editedTitle, editedContent, editedCustomTags, editedDate, editedCareerImpact, editedEngagementLevel, editState, editMode, item]);

  const handleSave = async () => {
    try {
      // Format the date to YYYY-MM-DD format
      const formattedDate = editedDate.split('T')[0];
      const dateTags = getDateTags(formattedDate, formattedDate, isReview(item) ? 'review' : 'note');

      const itemToSave: ItemUpdate = isReview(item)
        ? {
            type: 'review' as const,
            title: editedTitle,
            content: editedContent,
            custom_tags: editedCustomTags,
            date_tags: dateTags,
            review_goal: item.review_goal,
            output_style: item.output_style,
            word_count_target: item.word_count_target,
            review_date: formattedDate,
            user_id: item.user_id,
            review_text: editedContent,
            review_name: editedTitle,
            context_counts: item.context_counts,
            career_card_ids: item.career_card_ids,
            note_ids: item.note_ids,
          }
        : {
            type: 'note' as const,
            title: editedTitle,
            content: editedContent,
            custom_tags: editedCustomTags,
            date_tags: dateTags,
            note_date: formattedDate,
            career_impact: editedCareerImpact,
            engagement_level: editedEngagementLevel,
          };

      const result = await onSave(itemToSave);

      // Handle pending work experience link
      if (pendingWorkExperienceId && result.id) {
        try {
          // Use user from useAuthState instead of direct auth call
          if (!isAuthenticated || !user) {
            throw new Error('No active session');
          }

          const { error } = await supabase
            .from('content_links')
            .insert({
              source_id: result.id,
              source_type: itemToSave.type,
              target_id: pendingWorkExperienceId,
              target_type: CONTENT_TYPE.WORK_EXPERIENCE,
              user_id: user.id,
            });

          if (error) throw error;

          const linkedExp = workExperiences?.find(exp => exp.id === pendingWorkExperienceId);
          if (linkedExp) {
            setLinkedWorkExperience(linkedExp);
          }

          setPendingWorkExperienceId(null);
          showToast('Successfully linked work experience', 'success');
        } catch (error) {
          console.error('Error creating work experience link:', error);
          showToast('Failed to link work experience', 'error');
        }
      }

      setEditState(null);
      setEditMode(false);
      return result;
    } catch (error) {
      logger.error('Error saving item:', {
        error,
        itemType: item.type,
        editedDate
      });
      throw error;
    }
  };

  const handleCancel = () => {
    if (editState?.hasUnsavedChanges) {
      const confirmCancel = window.confirm('You have unsaved changes. Are you sure you want to cancel editing?');
      if (!confirmCancel) return;
    }
    
    // If this is a new item (no id), close the panel entirely
    if (!item.id) {
      onClose();
      return;
    }
    
    setEditMode(false);
    setEditState(null);
  };

  // Add this function to handle edit mode toggle
  const handleEditModeToggle = () => {
    if (editMode) {
      handleCancel();
    } else {
      setEditMode(true);
      // Focus will be handled by the useEffect below
    }
  };

  // Add this function to handle item close
  const handleClose = () => {
    if (editState?.hasUnsavedChanges) {
      const confirmClose = window.confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmClose) return;
    }
    onClose();
  };

  const generateTitle = async () => {
    if (!editedContent.trim()) {
      alert('Please add some content before generating a title');
      return;
    }

    setIsGeneratingTitle(true);
    try {
      // Use user/isAuthenticated from useAuthState instead of direct auth call
      if (!isAuthenticated) {
        throw new Error('No active session');
      }

      const { data, error } = await supabase.functions.invoke('generate-title', {
        body: { content: editedContent }
      });

      if (error) throw error;
      if (data?.title) {
        setEditedTitle(data.title);
      }
    } catch (error) {
      console.error('Error generating title:', error);
      alert('Failed to generate title. Please try again.');
    } finally {
      setIsGeneratingTitle(false);
    }
  };

  const generateTags = async () => {
    if (!editedContent.trim()) {
      alert('Please add some content before generating tags');
      return;
    }

    setIsGeneratingTags(true);
    try {
      const { data, error } = await supabase.functions.invoke('suggest-tags', {
        body: { 
          content: editedContent,
          existingTags: editedCustomTags
        }
      });

      if (error) throw error;
      if (data?.suggestedTags) {
        setSuggestedTags(data.suggestedTags);
      }
    } catch (error) {
      console.error('Error generating tags:', error);
      alert('Failed to generate tags. Please try again.');
    } finally {
      setIsGeneratingTags(false);
    }
  };

  const handleAddTag = () => {
    if (!newTag.trim()) return;
    
    const tag = newTag.trim();
    if (!editedCustomTags.includes(tag)) {
      setEditedCustomTags([...editedCustomTags, tag]);
    }
    setNewTag('');
  };

  const handleRemoveTag = (tagToRemove: string) => {
    if (tagToRemove === ACTIVE_TAG) return;
    setEditedCustomTags(editedCustomTags.filter(tag => tag !== tagToRemove));
  };

  const handleAddSuggestedTag = (tag: string) => {
    if (!editedCustomTags.includes(tag)) {
      setEditedCustomTags([...editedCustomTags, tag]);
      setSuggestedTags(prev => prev.filter(t => t !== tag));
    }
  };

  const handleArchiveToggle = async () => {
    if (editMode) return;

    const isArchived = editedCustomTags.includes(ARCHIVE_TAG);
    const newTags = isArchived
      ? editedCustomTags.filter(tag => tag !== ARCHIVE_TAG)
      : [ARCHIVE_TAG, ...editedCustomTags.filter(tag => tag !== ACTIVE_TAG)];

    const itemToSave = isNote(item)
      ? {
          ...item,
          title: editedTitle,
          content: editedContent,
          custom_tags: newTags,
          note_date: editedDate,
        }
      : {
          ...item,
          review_name: editedTitle,
          review_text: editedContent,
          custom_tags: newTags,
          review_date: editedDate,
        };

    try {
      await onSave(itemToSave);
      const itemType = isReview(item) ? 'Review' : 'Note';
      const action = isArchived ? 'unarchived' : 'archived';
      
      // Create an undo function that will reverse the archive action
      const handleUndo = async () => {
        const undoTags = isArchived 
          ? [ARCHIVE_TAG, ...editedCustomTags.filter(tag => tag !== ACTIVE_TAG)]
          : editedCustomTags.filter(tag => tag !== ARCHIVE_TAG);

        const undoItem = {
          ...itemToSave,
          custom_tags: undoTags,
        };

        try {
          await onSave(undoItem);
          showToast(`${itemType} ${isArchived ? 'archived' : 'unarchived'}`, 'success');
        } catch (error) {
          console.error('Error in undo archive:', error);
          showToast('Failed to undo archive action', 'error');
        }
      };

      showToast(
        `${itemType} ${action}`,
        'success',
        handleUndo
      );
    } catch (error) {
      console.error('Error toggling archive:', error);
      showToast(`Failed to ${isArchived ? 'unarchive' : 'archive'} ${isReview(item) ? 'review' : 'note'}`, 'error');
    }
  };

  const renderTag = (tag: string, index: number) => {
    const isSystemTag = tag === ACTIVE_TAG || tag === ARCHIVE_TAG;
    const tagStyle = tag === ARCHIVE_TAG 
      ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
      : tag === ACTIVE_TAG
        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
        : 'bg-gray-200 dark:bg-gray-700';

    return (
      <span
        key={index}
        className={`px-2 py-1 text-sm rounded-full flex items-center ${tagStyle}`}
      >
        {tag}
        {!isSystemTag && (
          <button
            onClick={() => handleRemoveTag(tag)}
            className="ml-1 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            ×
          </button>
        )}
      </span>
    );
  };

  const renderDateDisplay = () => {
    if (!editedDate) return null;
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400">
        Date: {formatDateOnlyWithTimezone(editedDate)}
      </div>
    );
  };

  const renderCreationDate = () => {
    if (!item.created_at) return null;
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400">
        Created: {formatWithTimezone(item.created_at)}
      </div>
    );
  };

  const renderDateInput = () => {
    return (
      <input
        type="date"
        value={editedDate}
        onChange={(e) => setEditedDate(e.target.value)}
        aria-label={`${isReview(item) ? 'Review' : 'Note'} date`}
        className="px-2 py-1 text-sm rounded-md bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600"
      />
    );
  };

  const {
    lastSaved,
    isSaving: isAutoSaving,
    error: autoSaveError,
  } = useAutoSave(editState, onSave);

  // Add draft recovery
  const {
    draft,
    showRecoveryDialog,
    setShowRecoveryDialog,
    handleRecover,
    handleDiscard,
  } = useDraftRecovery(item, (recoveredContent) => {
    // Update all editable fields from the recovered content
    setEditedTitle(recoveredContent.title);
    setEditedContent(recoveredContent.content);
    setEditedCustomTags(recoveredContent.custom_tags || []);
    
    if (isNote(recoveredContent)) {
      setEditedDate(recoveredContent.note_date);
      setEditedCareerImpact(recoveredContent.career_impact || 3);
      setEditedEngagementLevel(recoveredContent.engagement_level || 3);
    } else if (isReview(recoveredContent)) {
      setEditedDate(recoveredContent.review_date);
    }

    // Set edit mode and initialize edit state
    setEditMode(true);
    setEditState({
      hasUnsavedChanges: true,
      originalContent: item,
      currentContent: recoveredContent,
    });
  });

  // Add effect to focus editor when entering edit mode
  useEffect(() => {
    if (editMode && editorRef.current) {
      // Small delay to ensure the editor is mounted
      setTimeout(() => {
        const textarea = editorRef.current?.textarea;
        if (textarea) {
          textarea.focus();
          // Move cursor to end of content
          textarea.selectionStart = textarea.value.length;
          textarea.selectionEnd = textarea.value.length;
        }
      }, 100);
    }
  }, [editMode]);

  // Modify the fetchSuggestedTopics function
  const fetchSuggestedTopics = async (isMountedRef = { current: true }) => {
    if (!isNote(item)) return;

    // Don't make network requests if offline
    if (networkUtils.isOffline()) {
      if (isMountedRef.current) {
        setIsLoadingSuggestions(false);
      }
      return;
    }

    if (isMountedRef.current) {
      setIsLoadingSuggestions(true);
    }
    
    try {
      // Use user from useAuthState instead of direct auth call
      if (!isAuthenticated || !user) {
        throw new Error('No active session');
      }

      // First check for cached suggestions
      const { data: cachedSuggestions, error: cacheError } = await supabase
        .from('suggested_updates')
        .select('suggested_topics, context_hash, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);

      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return;

      // Always generate new suggestions for new notes to ensure they're relevant
      // This forces a regeneration regardless of cache age
      console.log('Calling edge function to generate new suggestions');
      const result = await supabase.functions.invoke('generate-update-prompt', {
        body: { 
          userId: user.id,
          checkContextOnly: true  // Just get the hash without generating suggestions
        }
      });

      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return;

      const { data: hashData, error: hashError } = result;
      
      if (hashError) {
        console.error('Error checking context hash:', hashError);
      }

      let shouldUseCache = false;
      
      // Only use cache if we have valid cached suggestions and the context hash matches
      if (!cacheError && 
          cachedSuggestions?.length > 0 && 
          cachedSuggestions[0].suggested_topics &&
          hashData?.contextHash) {
            
        console.log('Current context hash:', hashData.contextHash);
        console.log('Cached context hash:', cachedSuggestions[0].context_hash);
        
        // Use cache only if context hasn't changed (hash matches)
        if (hashData.contextHash === cachedSuggestions[0].context_hash) {
          console.log('Context hashes match, using cached suggestions');
          shouldUseCache = true;
          
          const suggestedTopics = cachedSuggestions[0].suggested_topics;
          const promptLines = suggestedTopics.split('\n');
          const mainPrompt = promptLines[0];
          const suggestions = promptLines.slice(2)
            .filter((line: string) => line.startsWith('•') || line.startsWith('-'))
            .map((line: string) => line.replace(/^[•-]\s*/, '').trim());

          if (isMountedRef.current) {
            setSuggestedTopics({
              prompt: mainPrompt,
              suggestions
            });
            setIsLoadingSuggestions(false);
          }
        } else {
          console.log('Context has changed, generating new suggestions');
        }
      } else {
        console.log('No valid cached suggestions or hash comparison, generating new ones');
      }

      // If cache is not usable or context has changed, get fresh suggestions
      if (!shouldUseCache) {
        // Full invocation to generate new suggestions
        console.log('Generating new suggestions from current context');
        const result = await supabase.functions.invoke('generate-update-prompt', {
          body: { 
            userId: user.id,
            forceRefresh: true  // Signal to bypass any server-side caching
          }
        });

        // Check if component is still mounted before updating state
        if (!isMountedRef.current) return;

        const { data, error } = result;

        if (error) {
          console.error('Edge function returned an error:', error);
          // Even if there's an error, check if we received a prompt in the response
          if (!data || !data.prompt) {
            throw error;
          }
          // Log the error but continue with the returned prompt
          console.log('Using prompt from error response:', data.prompt);
        }

        if (data && data.prompt) {
          console.log('Received prompt from edge function:', data.prompt.substring(0, 50) + '...');
          const promptLines = data.prompt.split('\n');
          const mainPrompt = promptLines[0];
          const suggestions = promptLines.slice(2)
            .filter((line: string) => line.startsWith('•') || line.startsWith('-'))
            .map((line: string) => line.replace(/^[•-]\s*/, '').trim());

          if (isMountedRef.current) {
            setSuggestedTopics({
              prompt: mainPrompt,
              suggestions
            });
          }
        } else {
          console.error('No prompt in the edge function response');
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error('Error fetching suggested topics:', error);
        // Don't show toast here as it could be confusing to users
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoadingSuggestions(false);
      }
    }
  };

  // Call fetchSuggestedTopics once when component mounts with a new note
  useEffect(() => {
    const isNewNote = isNote(item) && !item.id;
    
    if (isNewNote && isAuthenticated && user) {
      const isMounted = { current: true };
      
      const loadSuggestedTopics = async () => {
        try {
          setIsLoadingSuggestions(true);
          await fetchSuggestedTopics(isMounted);
        } catch (error) {
          console.error('Error loading suggested topics:', error);
          if (isMounted.current) {
            setIsLoadingSuggestions(false);
          }
        }
      };
      
      loadSuggestedTopics();
      
      return () => {
        isMounted.current = false;
      };
    }
  // Add proper dependencies but ensure they won't cause infinite loops
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, item.type, isAuthenticated, user?.id]);

  // Store current state in sessionStorage when content changes
  useEffect(() => {
    if (editMode && item.id) {
      try {
        sessionStorage.setItem(`unsaved_content_${item.id}`, editedContent);
        sessionStorage.setItem(`edit_mode_${item.id}`, 'true');
      } catch (e) {
        // Ignore storage errors
      }
    }
  }, [editedContent, editMode, item.id]);

  // Handle app resume event
  useEffect(() => {
    const handleAppResume = () => {
      if (item.id) {
        try {
          const savedContent = sessionStorage.getItem(`unsaved_content_${item.id}`);
          const wasInEditMode = sessionStorage.getItem(`edit_mode_${item.id}`) === 'true';

          if (savedContent && wasInEditMode) {
            setEditedContent(savedContent);
            setEditMode(true);
            showToast('Restored unsaved changes', 'info');
          }
        } catch (e) {
          // Ignore storage errors
        }
      }
    };

    window.addEventListener('app:resume', handleAppResume);
    return () => window.removeEventListener('app:resume', handleAppResume);
  }, [item.id, showToast]);

  // Clean up storage when component unmounts or item changes
  useEffect(() => {
    return () => {
      if (item.id) {
        try {
          sessionStorage.removeItem(`unsaved_content_${item.id}`);
          sessionStorage.removeItem(`edit_mode_${item.id}`);
        } catch (e) {
          // Ignore storage errors
        }
      }
    };
  }, [item.id]);

  return (
    <motion.div
      // Skip entry animation if we're switching between panels
      initial={skipAnimation ? false : { x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }} // Always animate exit
      transition={{ type: 'tween', duration: 0.3 }}
      className="h-full flex-1 flex flex-col overflow-hidden bg-white dark:bg-gray-800"
    >
      {/* Add the recovery dialog */}
      {draft && (
        <DraftRecoveryDialog
          isOpen={showRecoveryDialog}
          onClose={() => setShowRecoveryDialog(false)}
          currentVersion={item}
          draftVersion={draft.content}
          lastSaved={new Date(draft.last_saved)}
          onRecover={handleRecover}
          onDiscard={handleDiscard}
        />
      )}

      <div className="sticky top-0 bg-white dark:bg-gray-800 z-10">
        <div className="flex justify-between items-start p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="grow pr-5">
            {editMode ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="relative grow">
                    <input
                      type="text"
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      className={`w-full text-xl font-bold bg-transparent border-b ${
                        !editedTitle.trim() && editState?.hasUnsavedChanges
                          ? 'border-red-500 dark:border-red-500'
                          : 'border-gray-300 dark:border-gray-600'
                      } focus:outline-none focus:border-blue-500 pr-8`}
                      placeholder="Enter title... (required)"
                    />
                    <button
                      onClick={generateTitle}
                      disabled={isGeneratingTitle}
                      className="absolute right-0 top-0 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      title="Generate title"
                    >
                      <IoSparklesOutline size={20} className={isGeneratingTitle ? 'animate-pulse' : ''} />
                    </button>
                    {!editedTitle.trim() && editState?.hasUnsavedChanges && (
                      <p className="mt-1 text-sm text-red-500">Title is required</p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleCancel}
                      className="px-3 py-1 text-sm rounded-md bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <SaveButton
                      onClick={async () => {
                        await handleSave();
                      }}
                      hasChanges={editState?.hasUnsavedChanges || false}
                      disabled={!editedTitle.trim()}
                    />
                    <button
                      onClick={handleClose}
                      className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
                      title="Close panel"
                    >
                      <IoCloseOutline size={20} />
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <UnsavedChangesDot visible={editState?.hasUnsavedChanges || false} />
                  <AutoSaveStatus
                    lastSaved={lastSaved}
                    isSaving={isAutoSaving}
                    error={autoSaveError}
                  />
                </div>
                <div className="flex items-center gap-2">
                  {renderDateInput()}
                  {renderCreationDate()}
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold">{item.title}</h2>
                  <div className="flex space-x-2">
                    {!editMode ? (
                      <>
                        <button
                          onClick={handleArchiveToggle}
                          className={`p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full ${
                            editedCustomTags.includes(ARCHIVE_TAG) ? 'text-gray-600' : ''
                          }`}
                          title={editedCustomTags.includes(ARCHIVE_TAG) ? 'Unarchive note' : 'Archive note'}
                        >
                          <IoArchiveOutline size={20} />
                        </button>
                        <button
                          onClick={handleEditModeToggle}
                          className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
                          title="Edit note"
                        >
                          <IoPencilOutline size={20} />
                        </button>
                        <button
                          onClick={() => onDelete(item.id)}
                          className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full text-red-600"
                          title="Delete note"
                        >
                          <IoTrashOutline size={20} />
                        </button>
                        <button
                          onClick={handleClose}
                          className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
                          title="Close panel"
                        >
                          <IoCloseOutline size={20} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={handleCancel}
                          className="px-3 py-1 text-sm rounded-md bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                        <SaveButton
                          onClick={async () => {
                            await handleSave();
                          }}
                          hasChanges={editState?.hasUnsavedChanges || false}
                          disabled={!editedTitle.trim()}
                        />
                        <button
                          onClick={handleClose}
                          className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
                          title="Close panel"
                        >
                          <IoCloseOutline size={20} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {item.type === 'review' ? (
                  <div className="space-y-2 mt-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      {renderDateDisplay()}
                      {renderCreationDate()}
                    </div>
                    <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                      <div className="grid grid-cols-3 gap-4 text-gray-900 dark:text-gray-100">
                        <div>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">Goal: </span>
                          <span className="text-sm text-gray-700 dark:text-gray-300">{item.review_goal}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">Style: </span>
                          <span className="text-sm text-gray-700 dark:text-gray-300">{formatOutputStyle(item.output_style)}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">Word Count Target: </span>
                          <span className="text-sm text-gray-700 dark:text-gray-300">{item.word_count_target}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mt-2">
                    {renderDateDisplay()}
                    {renderCreationDate()}
                  </div>
                )}
                {/* Show tags in header only in view mode */}
                {editedCustomTags.length > 0 && !editMode && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {editedCustomTags.map((tag, index) => renderTag(tag, index))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Suggested Topics section in edit mode for new notes */}
        {editMode && isNote(item) && !item.id && (isLoadingSuggestions || suggestedTopics) && (
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <h4 className="text-sm font-medium">Suggested Topics</h4>
                <div className="flex items-center">
                  <button
                    onClick={async () => {
                      if (isLoadingSuggestions) return; // Prevent multiple requests
                      
                      setIsLoadingSuggestions(true);
                      try {
                        // Force a new fetch by calling the edge function directly
                        await fetchSuggestedTopics();
                        // Success toast for better feedback
                        showToast('Suggested topics refreshed', 'success');
                      } catch (error) {
                        console.error('Error refreshing suggested topics:', error);
                        showToast('Failed to refresh suggestions', 'error');
                      } finally {
                        setIsLoadingSuggestions(false);
                      }
                    }}
                    className={`p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full mr-1 ${isLoadingSuggestions ? 'opacity-50' : ''}`}
                    title="Refresh suggestions"
                    disabled={isLoadingSuggestions}
                    aria-label="Refresh suggested topics"
                  >
                    <IoRefreshOutline 
                      size={18} 
                      className={isLoadingSuggestions ? "animate-spin" : ""} 
                    />
                  </button>
                  <button
                    onClick={() => setIsSuggestedTopicsExpanded(!isSuggestedTopicsExpanded)}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
                    title={isSuggestedTopicsExpanded ? "Collapse suggestions" : "Expand suggestions"}
                  >
                    {isSuggestedTopicsExpanded ? <IoChevronUpOutline size={20} /> : <IoChevronDownOutline size={20} />}
                  </button>
                </div>
              </div>
              <motion.div
                initial={false}
                animate={{ height: isSuggestedTopicsExpanded ? "auto" : 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                {isLoadingSuggestions ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent"></div>
                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Generating suggestions...</span>
                  </div>
                ) : suggestedTopics ? (
                  <>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{suggestedTopics.prompt}</p>
                    <div className="space-y-1">
                      {suggestedTopics.suggestions.map((suggestion, index) => {
                        const isUsed = usedSuggestions.has(suggestion);
                        return (
                          <button
                            key={index}
                            onClick={() => {
                              if (!isUsed) {
                                const newContent = editedContent.trim() + (editedContent.trim() ? '\n\n' : '') + suggestion;
                                setEditedContent(newContent);
                                setUsedSuggestions(prev => new Set([...prev, suggestion]));
                              }
                            }}
                            disabled={isUsed}
                            className={`w-full text-left px-3 py-2 text-sm rounded-md flex items-center gap-2 ${
                              isUsed 
                                ? 'text-gray-400 dark:text-gray-500 cursor-default'
                                : 'hover:bg-gray-200 dark:hover:bg-gray-700'
                            }`}
                          >
                            <IoSparklesOutline className="shrink-0" />
                            <span>{suggestion}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                ) : null}
              </motion.div>
            </div>
          </div>
        )}

        {/* Content section - Moved up for new notes */}
        {editMode && isNote(item) && !item.id ? (
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div data-color-mode={darkMode ? "dark" : "light"}>
              <MarkdownEditor
                value={editedContent}
                onChange={(value) => setEditedContent(value || '')}
                preview="edit"
                height={300}
                darkMode={darkMode}
                textareaProps={{
                  spellCheck: true,
                  autoCorrect: "on"
                }}
              />
            </div>
            <div className="flex justify-end mt-2">
              <VoiceRecordingControl
                onTranscriptReceived={(text) => {
                  const currentContent = editedContent || '';
                  const newContent = currentContent + (currentContent ? ' ' : '') + text;
                  setEditedContent(newContent);
                }}
              />
            </div>
          </div>
        ) : null}

        {/* Tags section in edit mode */}
        {editMode && (
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="space-y-2">
              <div>
                <h4 className="text-sm font-medium mb-2">Tags</h4>
                <div className="flex flex-wrap gap-2 mb-2">
                  {editedCustomTags.map((tag, index) => renderTag(tag, index))}
                </div>
                <div className="flex mb-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                    placeholder="Add a new tag"
                    className="grow p-2 rounded-l-lg border dark:bg-gray-700 dark:border-gray-600"
                  />
                  <button
                    onClick={handleAddTag}
                    className="px-4 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700"
                  >
                    Add
                  </button>
                </div>
                <button
                  onClick={generateTags}
                  disabled={isGeneratingTags}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-500"
                >
                  {isGeneratingTags ? (
                    <>
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                      Generating suggestions...
                    </>
                  ) : (
                    <>
                      <IoSparklesOutline size={16} />
                      Suggest tags
                    </>
                  )}
                </button>
              </div>
              {suggestedTags.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm text-gray-500 mb-1">Suggested tags:</p>
                  <div className="flex flex-wrap gap-2">
                    {suggestedTags.map((tag, index) => (
                      <button
                        key={index}
                        onClick={() => handleAddSuggestedTag(tag)}
                        className="text-xs px-2.5 py-0.5 rounded border border-blue-300 hover:bg-blue-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                      >
                        + {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {isNote(item) && (
          <ImpactMetricsSection
            careerImpact={{
              value: editedCareerImpact,
              last_updated: new Date().toISOString()
            }}
            engagementLevel={{
              value: editedEngagementLevel,
              last_updated: new Date().toISOString()
            }}
            isEditMode={editMode}
            cardType="note"
            onMetricChange={(type: 'career_impact' | 'engagement_level', value: number) => {
              if (type === 'career_impact') {
                setEditedCareerImpact(value);
                if (editState && isNote(editState.currentContent)) {
                  setEditState({
                    ...editState,
                    hasUnsavedChanges: true,
                    currentContent: {
                      ...editState.currentContent,
                      career_impact: value
                    }
                  });
                }
              } else {
                setEditedEngagementLevel(value);
                if (editState && isNote(editState.currentContent)) {
                  setEditState({
                    ...editState,
                    hasUnsavedChanges: true,
                    currentContent: {
                      ...editState.currentContent,
                      engagement_level: value
                    }
                  });
                }
              }
            }}
            historyEntries={
              'impact_history' in item && 
              typeof item.impact_history === 'object' && 
              item.impact_history !== null && 
              Array.isArray((item.impact_history as { entries?: unknown[] }).entries) 
                ? (item.impact_history as { entries: ImpactHistoryEntry[] }).entries 
                : []
            }
          />
        )}

        {/* Content section - Original position for existing notes */}
        {(!editMode || (editMode && (!isNote(item) || item.id))) && (
          <div className="flex-1">
            {editMode ? (
              <div className="p-4">
                <div data-color-mode={darkMode ? "dark" : "light"}>
                  <MarkdownEditor
                    value={editedContent}
                    onChange={(value) => setEditedContent(value || '')}
                    preview="edit"
                    height={300}
                    darkMode={darkMode}
                    textareaProps={{
                      spellCheck: true,
                      autoCorrect: "on"
                    }}
                  />
                </div>
                <div className="flex justify-end mt-2">
                  <VoiceRecordingControl
                    onTranscriptReceived={(text) => {
                      const currentContent = editedContent || '';
                      const newContent = currentContent + (currentContent ? ' ' : '') + text;
                      setEditedContent(newContent);
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="p-4">
                <div className="prose dark:prose-invert max-w-none">
                  <MarkdownEditor
                    value={item.content}
                    onChange={() => {}}
                    preview="preview"
                    darkMode={darkMode}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Links Section - Available for all item types */}
        <div className={`p-4 border-b border-gray-200 dark:border-gray-700 ${editMode ? 'bg-gray-50 dark:bg-gray-900' : ''}`}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <IoLinkOutline />
              Linked Content
            </h3>
          </div>

          {/* Work Experience Link */}
          <div className="space-y-2">
            <h4 className="text-xs text-gray-500 dark:text-gray-400">Work Experience</h4>
            {isLoadingLinkedContent || workExperiencesLoading ? (
              <LinkedContentPreview
                content={{
                  id: 'loading',
                  type: 'work_experience',
                  preview: {}
                }}
                isLoading={true}
              />
            ) : linkedWorkExperience ? (
              <LinkedContentPreview
                content={linkedWorkExperience}
                onUnlink={editMode ? handleUnlinkWorkExperience : undefined}
              />
            ) : (
              <div className="relative">
                <select
                  onChange={(e) => handleWorkExperienceSelect(e.target.value)}
                  value=""
                  className="w-full p-2 rounded border text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-black dark:text-white"
                  aria-label="Select work experience to link"
                >
                  <option value="">Select work experience...</option>
                  {workExperiences?.map((exp) => (
                    <option key={exp.id} value={exp.id}>
                      {exp.preview.position} at {exp.preview.company}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Focus Linkage - collapsible section in edit mode */}
          {editMode && focus && item.id && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs text-gray-500 dark:text-gray-400">
                  Link to {focus.company} - {focus.role}
                </h4>
                <button
                  onClick={() => setIsFocusLinkageExpanded(!isFocusLinkageExpanded)}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
                  title={isFocusLinkageExpanded ? "Collapse focus linking" : "Expand focus linking"}
                >
                  {isFocusLinkageExpanded ? <IoChevronUpOutline size={16} /> : <IoChevronDownOutline size={16} />}
                </button>
              </div>
              
              <motion.div
                initial={false}
                animate={{ height: isFocusLinkageExpanded ? "auto" : 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="space-y-4">
                  <GoalSelector 
                    cardId={item.id} 
                    sourceType={item.type}
                    className=""
                  />
                  
                  {activeRubric && (
                    <RubricCriteriaSelector 
                      cardId={item.id} 
                      sourceType={item.type}
                      className=""
                    />
                  )}
                </div>
              </motion.div>
            </div>
          )}
          
          {/* View mode display of linked goals and criteria */}
          {!editMode && focus && item.id && (linkedGoalIds.length > 0 || linkedRubricCriteria.length > 0) && (
            <div className="mt-4">
              <h4 className="text-xs text-gray-500 dark:text-gray-400 mb-2">Active Internship</h4>
              <div className={`${darkMode ? 'bg-gray-800' : 'bg-gray-50'} ${darkMode ? 'border-gray-700' : 'border-gray-200'} border rounded-lg p-3`}>
                <div className="space-y-3">
                  {/* Focus Info */}
                  <div className="font-medium text-sm">
                    {focus.company} - {focus.role}
                  </div>
                  
                  {/* Linked Goals */}
                  {linkedGoalIds.length > 0 && goals && (
                    <div>
                      <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Linked Goals:</h5>
                      <div className="space-y-1 ml-2">
                        {linkedGoalIds.map(goalId => {
                          const goal = goals.find(g => g.id === goalId);
                          if (!goal) return null;
                          return (
                            <div key={goalId} className="flex items-center gap-2 text-sm">
                              <CheckIcon className="w-3 h-3 text-green-500 shrink-0" />
                              <span className="text-gray-700 dark:text-gray-300">
                                {goal.goal_text}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Linked Rubric Criteria */}
                  {linkedRubricCriteria.length > 0 && activeRubric && (
                    <div>
                      <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Linked Evaluation Criteria:</h5>
                      <div className="space-y-2 ml-2">
                        {activeRubric.categories.map(category => {
                          // Find criteria in this category that are linked
                          const linkedInCategory = linkedRubricCriteria.filter(criterionId => {
                            // Check if this criterion belongs to this category
                            if (category.criteria_items) {
                              return category.criteria_items.some(item => item.id === criterionId);
                            }
                            return false;
                          });
                          
                          if (linkedInCategory.length === 0) return null;
                          
                          return (
                            <div key={category.id}>
                              <h6 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">{category.name}</h6>
                              <div className="space-y-1 mt-1">
                                {category.criteria_items?.map(criterion => {
                                  if (!linkedRubricCriteria.includes(criterion.id)) return null;
                                  return (
                                    <div key={criterion.id} className="flex items-start gap-2 text-sm">
                                      <CheckIcon className="w-3 h-3 text-blue-500 shrink-0 mt-0.5" />
                                      <span className="text-gray-700 dark:text-gray-300">
                                        {criterion.text}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Show message for unsaved notes */}
          {focus && !item.id && (
            <div className="mt-4">
              <h4 className="text-xs text-gray-500 dark:text-gray-400 mb-2">Evidence Linking</h4>
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  Save the note to link it to goals and rubric categories
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}; 